import React, { useState, useEffect, useRef } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { showAlert } from '../../../functions/show_alert';

export default function WizardErrorCorrectionFeedbackSourceForm({ onDataChange, initialData }) {
    const [text, setText] = useState(initialData.text || '');
    const [image, setImage] = useState(initialData.image || null);
    const [fileName, setFileName] = useState(initialData.fileName || null);
    const onDataChangeRef = useRef(onDataChange);

    // Update ref when callback changes
    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    // Only call onDataChange when actual data changes, not when callback changes
    useEffect(() => {
        if (onDataChangeRef.current) {
            onDataChangeRef.current({ 
                text,
                image,
                fileName
            });
        }
    }, [text, image, fileName]);

    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    const compressImage = (file, maxWidth = 400, maxHeight = 400, maxSizeKB = 300) => {
        return new Promise((resolve, reject) => {
            // For PDF files, check size and reject if too large
            if (file.type === 'application/pdf') {
                if (file.size > maxSizeKB * 1024) {
                    reject(new Error(`PDF file size must be less than ${maxSizeKB}KB`));
                    return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
                    resolve(base64Match ? base64Match[1] : dataUrl);
                };
                reader.readAsDataURL(file);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Calculate new dimensions
                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Try different quality levels to meet size requirement
                    let quality = 0.7;
                    let compressedDataUrl = canvas.toDataURL(file.type, quality);
                    let base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                    let sizeKB = (base64Data.length * 3) / 4 / 1024; // Approximate size in KB

                    // Reduce quality if still too large
                    while (sizeKB > maxSizeKB && quality > 0.3) {
                        quality -= 0.1;
                        compressedDataUrl = canvas.toDataURL(file.type, quality);
                        base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                        sizeKB = (base64Data.length * 3) / 4 / 1024;
                    }

                    if (sizeKB > maxSizeKB) {
                        reject(new Error(`Image is too large even after compression (${sizeKB.toFixed(0)}KB). Please use a smaller image.`));
                        return;
                    }

                    resolve(base64Data);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = reader.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleFileUpload = async (file) => {
        // Check original file size (before compression)
        if (file.size > 10000000) {
            showAlert(false, 'The file size is more than 10 MB');
            return;
        }

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showAlert(false, 'Only .jpg, .png, & .pdf files will be accepted');
            return;
        }

        try {
            // Compress image before storing (max 300KB after compression)
            const base64Data = await compressImage(file, 400, 400, 300);
            setImage(base64Data);
            setFileName(file.name);
        } catch (error) {
            console.error('Error processing file:', error);
            showAlert(false, error.message || 'Error processing file. Please try again.');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileUpload(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <div className="wizard-error-correction-feedback-form">
            <h2 className="wizard-step-heading">Add Feedback Source Details</h2>
            <div className="error-correction-form-container">
                <div className="form-field">
                    <label className="field-label">Text</label>
                    <textarea
                        className="form-textarea"
                        placeholder="Enter text"
                        value={text}
                        onChange={handleTextChange}
                        rows={2}
                    />
                </div>

                <div className="form-field">
                    <label className="field-label">Upload Image</label>
                    <div
                        className={`file-upload-area ${fileName ? 'has-file' : ''}`}
                        onClick={() => {
                            document.getElementById('error-correction-source-file-input').click();
                        }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        <input
                            id="error-correction-source-file-input"
                            type="file"
                            style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    handleFileUpload(e.target.files[0]);
                                }
                            }}
                        />
                        <CloudUploadIcon className="upload-icon" />
                        <p className="upload-text">Upload the file here</p>
                        <p className="upload-hint">(Only .jpg, .png, & .pdf files will be accepted)</p>
                        {fileName && (
                            <p className="uploaded-file">{fileName}</p>
                        )}
                    </div>
                    {!fileName && (
                        <p className="no-files-text">no files uploaded yet</p>
                    )}
                </div>
            </div>
        </div>
    );
}

